import { Component, OnInit } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { MenuController, Platform } from '@ionic/angular';
import { NetworkService } from './services/network.service';
import { ServerVersionService } from './services/server-version.service';
import { SwUpdate } from '@angular/service-worker';
import { ApiService } from './services/api.service';
import { Select, Store as NgxsStore } from '@ngxs/store'
import { Observable, firstValueFrom } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { AppConfig } from './state-man/models/app-config.model';
import { AppConfigState } from './state-man/state/app-config.state';
import { AppConfigActions } from './state-man/actions/app-config.actions';
import { LoggerService } from './services/logger.service';
import { StorageService } from './services/storage.service';
import { Store } from './state-man/models/store.model';
import { Mutex } from 'async-mutex';
import { AppActions } from './state-man/actions/app.actions';
import { UiService } from './services/ui.service';
import { DateTime } from "luxon";
import { UserActions } from './state-man/actions/user.actions';
import { ConfigState } from './state-man/state/config.state';
import { Config } from './state-man/models/config.model';
import { getHaversineDistance } from './utils/general-utils';
import { StoreActions } from './state-man/actions/store.actions';
import { StoreState } from './state-man/state/store.state';
import { CallState } from './state-man/state/call.state';
import { CallActions } from './state-man/actions/call.actions';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {
  @Select(UserState.getUser) user$: Observable<User>  
  @Select(ConfigState.getConfig) configs$: Observable<Config>
  @Select(AppConfigState.getAppConfig) appConfig$: Observable<AppConfig>
  @Select(StoreState.getAll) stores$: Observable<Store[]>
  @Select(StoreState.getIsRefreshing) isRefreshingStoreState$: Observable<boolean>
  @Select(CallState.getIsRefreshing) isRefreshingCallState$: Observable<boolean>;
  @Select(ConfigState.getIsRefreshing) isRefreshingConfigState$: Observable<boolean>;

  state_version:string
  is_refreshing: boolean = false
  block_mutex: Mutex
  store_state_refreshing: boolean = true
  call_state_refreshing: boolean = true
  config_state_refreshing: boolean = true
  DateTime = DateTime;
  private watchId: string | null = null;

  constructor(
    public networkService: NetworkService,
    private platform: Platform,
    public serverVersionService: ServerVersionService,
    private swUpdate: SwUpdate,
    private apiService: ApiService,
    private store: NgxsStore,
    private storageService: StorageService,
    private uiService: UiService,
    private menu: MenuController
  ) {
    this.block_mutex = new Mutex()
    this.state_version = '1.0.0'
    this.platform.ready().then(async () => {      
      this.networkService.startWatching();
      this.serverVersionService.startWatching();
    });
    window.addEventListener('load', () => {
      window.history.pushState(null, null!, window.location.href);
    });

    window.addEventListener('popstate', () => {
      window.history.pushState(null, null!, window.location.href);
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.available.subscribe(() => {
        this.updatePrompt();
      });
    }

    this.networkService.events.subscribe(async (status: any) => {
      try {
        this.networkStatusChange();
      } catch (error) {          
        LoggerService.log(error)   
        await this.uiService.showToast('An unexpected error occurred and has been sent to the support team (ntwk).')    
      } 

    });

    this.storageService.events.subscribe(async (status: any) => {
      if (this.storageService.is_initialized) {        
        const val: AppConfig = await firstValueFrom(this.appConfig$)        
        if (val?.state_version !== this.state_version) {
          LoggerService.log('need to clear storage and set state version')        
          await this.storageService.clear()
          this.store.dispatch(new AppConfigActions.Set({state_version: this.state_version}))
        } 
      } else {
        LoggerService.log('not initialized')
      }
    })   
    
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    };

    try {
      console.info('watchPosition started')
      this.watchId = await Geolocation.watchPosition(options, async (data: any, error?: any) => {
        try {
          if (error) {
            var errorType = 'Unknown'
            if(error.code == GeolocationPositionError.PERMISSION_DENIED) {
              errorType = 'Permission Denied'
            } else if(error.code == GeolocationPositionError.POSITION_UNAVAILABLE) {
              errorType = 'Position Unavailable'
            } else if(error.code == GeolocationPositionError.TIMEOUT) {
              errorType = 'Timeout'
            }
            console.error('watchPosition error: ' + error?.code + ' (' + errorType + '): ' + error?.message);
            return;
          }
          
          if (data && data.coords) {        
            const latitude = data.coords.latitude.toFixed(4);
            const longitude = data.coords.longitude.toFixed(4);
            const user = await firstValueFrom(this.user$)
            const isStoreDistanceUpdateNeeded = user?.latitude && user?.longitude 
              ? await this.isStoreDistanceUpdateNeeded(Number(latitude), Number(longitude), Number(user.latitude), Number(user.longitude)) 
              : false
            this.store.dispatch(new UserActions.SetLocation({latitude, longitude}));
            if(isStoreDistanceUpdateNeeded) {
              this.updateStoreDistances(Number(latitude), Number(longitude), user)
            }
          }
        } catch(e) {
          console.error('Error in watchPosition callback:', e);
        }
      });
    } catch (error) {
      console.error('Error starting watchPosition:', error);
      await this.uiService.showToast('Failed to start location tracking.');
    }

    this.checkRefreshing()

    this.isRefreshingStoreState$.subscribe(async (val: boolean) => {      
      this.store_state_refreshing = val
      this.checkRefreshing()
    });

    this.isRefreshingCallState$.subscribe(async (val: boolean) => {
      this.call_state_refreshing = val
      this.checkRefreshing()
    });

    this.isRefreshingConfigState$.subscribe(async (val: boolean) => {
      this.config_state_refreshing = val
      this.checkRefreshing()
    });
  }
  
  async ngOnDestroy(): Promise<void> {
    // Clear the watch when the component is destroyed
    if (this.watchId) {
      try {
        await Geolocation.clearWatch({ id: this.watchId });
        console.info('watchPosition cleared with ID:', this.watchId);
        this.watchId = null;
      } catch (error) {
        console.error('Error clearing watchPosition:', error);
      }
    }
  }


  closeMenu() {
    this.menu.close();
  }

  async isStoreDistanceUpdateNeeded(newLatitude: number, newLongitude: number, oldLatitude: number, oldLongitude: number) {
    const config: Config = await firstValueFrom(this.configs$)
    if(!config?.distance_threshold || config.distance_threshold == undefined || config.distance_threshold <= 0) {
      console.warn('Distance threshold not downloaded yet. Skipping store distance update.');
      return false
    }
    if (newLatitude && newLongitude && oldLatitude && oldLongitude) {
      const distance = getHaversineDistance(newLatitude, newLongitude, oldLatitude, oldLongitude)
      if (distance > config?.distance_threshold) {
        return true
      }
    }
    return false
  }

  async updateStoreDistances(newLatitude: number, newLongitude: number, user: User) {
    const stores: Store[] = await firstValueFrom(this.stores$)
    const updatedStores: Store[] = []
    if(stores && stores.length > 0) {
      stores.forEach(async (store) => {
        const updatedStore = Store.buildOne(store)
        updatedStore.distance = getHaversineDistance(newLatitude, newLongitude, store.latitude, store.longitude)
        updatedStores.push(updatedStore)
      })               
      this.store.dispatch(new StoreActions.Set({ stores: updatedStores, user }))
    }
  }

  async checkRefreshing() {
    const release = await this.block_mutex.acquire();
    try {
      if (    
        this.store_state_refreshing
        || this.call_state_refreshing
        || this.config_state_refreshing
        )
      { 
        if (!this.is_refreshing) {
          this.is_refreshing = true
          this.store.dispatch(new AppActions.Set({is_refreshing: true}))          
        }
      }    
      else {
        if (this.is_refreshing) {
          this.is_refreshing = false;
          this.store.dispatch(new AppActions.Set({is_refreshing: false}))                
        }
      }    
    } catch(e) {
      console.error('Error checking refreshing state:', e);
      this.store.dispatch(new AppActions.Set({is_refreshing: false}))
    } finally {
      release()
    }
  }

  resetRefreshing() {    
    this.is_refreshing = false;
    this.store_state_refreshing = false
    this.call_state_refreshing = false
    this.config_state_refreshing = false
    this.store.dispatch(new AppActions.Set({is_refreshing: false}))
  }

  async networkStatusChange() {       
    this.store.dispatch(new CallActions.IncrementPendingCount())
  }

  updateConfirmed() {
    this.apiService.goHome();
  }

  async updatePrompt() {
      const retVal = await this.uiService.confirmPrompt('New Version Available', 'Click OK to update the software to the latest version', "OK", '')
      if (retVal) {
        this.updateConfirmed()
      } else {
         this.updateConfirmed()
      }
    }
}
